import { snakeCase } from 'change-case';

import type { CallListSchema, PaginatedResponseCallListSchema } from '@stuut/api-sdk/models';
import type { GetCallsRequest } from '@stuut/api-sdk/models/operations';
import { Call } from '@stuut/api/models';
import type { CallQueryParams } from '@stuut/api/query';

export interface LegacyCallListPage {
  current_page: number;
  data: Call[];
  next_page: number | null;
  total_records?: number | null;
}

const toDate = (value?: Date | { toDate?: () => Date } | null): Date | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) return value;
  return value.toDate?.();
};

interface DecimalLike {
  isZero: () => boolean;
  toFixed: () => string;
  toNumber: () => number;
}

const isDecimalLike = (value: object): value is DecimalLike => {
  const candidate = value as Partial<Record<keyof DecimalLike, unknown>>;

  return (
    typeof candidate.isZero === 'function' &&
    typeof candidate.toFixed === 'function' &&
    typeof candidate.toNumber === 'function'
  );
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number') return value;
  if (typeof value === 'string') return Number(value);
  if (value && typeof value === 'object' && isDecimalLike(value)) return value.toNumber();

  return 0;
};

const toLegacyDate = (value: Date | string | null | undefined) => value ?? null;

const snakeCaseAttributes = (value: object): Record<string, unknown> => {
  return Object.fromEntries(Object.entries(value).map(([key, child]) => [snakeCase(key), child]));
};

type CallListInvoice = NonNullable<CallListSchema['invoices']>[number];
type CallListTask =
  | NonNullable<CallListSchema['displayTasks']>[number]
  | NonNullable<CallListSchema['tasks']>[number];
type CallListPartner = NonNullable<CallListSchema['partner']>;
type CallListClassification = NonNullable<CallListSchema['classifications']>[number];
type CallListWorkflow = NonNullable<CallListSchema['workflow']>;
type CallListWorkflowRun = NonNullable<CallListSchema['workflowRun']>;

interface LegacyDatedSummary {
  createdAt: Date | string | null | undefined;
  modifiedAt: Date | string | null | undefined;
}

function toLegacyJson<Summary extends LegacyDatedSummary>(
  summary: Summary | null | undefined,
): Record<string, unknown> | null {
  if (!summary) return null;

  return {
    ...snakeCaseAttributes(summary),
    created_at: toLegacyDate(summary.createdAt),
    modified_at: toLegacyDate(summary.modifiedAt),
  };
}

const invoiceSummaryToLegacyJson = (invoice: CallListInvoice) => {
  return {
    ...snakeCaseAttributes(invoice),
    amount_due: toNumber(invoice.amountDue),
    amount_paid: toNumber(invoice.amountPaid),
    amount_remaining: toNumber(invoice.amountRemaining),
    created_at: toLegacyDate(invoice.createdAt),
    due_date: toLegacyDate(invoice.dueDate),
    issued_at: toLegacyDate(invoice.issuedAt),
    modified_at: toLegacyDate(invoice.modifiedAt),
    paid_at: toLegacyDate(invoice.paidAt),
    partner: toLegacyJson(invoice.partner),
    pending_amount_remaining: toNumber(invoice.pendingAmountRemaining),
    total_amount: toNumber(invoice.totalAmount),
  };
};

const workflowRunSummaryToLegacyJson = (workflowRun: CallListWorkflowRun | null | undefined) => {
  if (!workflowRun) return null;

  return {
    ...toLegacyJson(workflowRun),
    scheduled_for: toLegacyDate(workflowRun.scheduledFor),
  };
};

export const callQueryParamsToSdkRequest = (params: CallQueryParams): GetCallsRequest => ({
  after: toDate(params.after),
  before: toDate(params.before),
  businessUnitIdIn: params.business_unit_id__in,
  classification: params.classification as GetCallsRequest['classification'],
  direction: params.direction as GetCallsRequest['direction'],
  invoiceId: params.invoice,
  limit: params.limit,
  page: params.page,
  partnerId: params.partner,
  sentiment: params.sentiment as GetCallsRequest['sentiment'],
  workflowId: params.workflow,
});

export const callListSchemaToCall = (call: CallListSchema): Call => {
  const tasks = call.displayTasks ?? call.tasks ?? [];

  return Call.fromJson({
    ...snakeCaseAttributes(call),
    classifications: call.classifications.map(toLegacyJson<CallListClassification>),
    created_at: toLegacyDate(call.createdAt),
    end_time: toLegacyDate(call.endTime),
    invoices: call.invoices.map(invoiceSummaryToLegacyJson),
    modified_at: toLegacyDate(call.modifiedAt),
    partner: toLegacyJson(call.partner),
    partner_id: call.partner?.id ?? null,
    start_time: toLegacyDate(call.startTime),
    tasks: tasks.map(toLegacyJson<CallListTask>),
    workflow: toLegacyJson<CallListWorkflow>(call.workflow),
    workflow_run: workflowRunSummaryToLegacyJson(call.workflowRun),
  });
};

export const callListResponseToLegacyPage = (
  response: PaginatedResponseCallListSchema,
): LegacyCallListPage => ({
  current_page: response.currentPage,
  data: response.data.map(callListSchemaToCall),
  next_page: response.nextPage,
  total_records: response.totalRecords,
});
